import React from "react";
import styled, { useTheme } from "styled-components";
import { bool, func } from "prop-types";
import { useTranslation } from "@lib/i18n";

import ResultItemLogo from "@components/TestDrive/FilterResultItems/ResultItemLogo";
import Typography from "@components/shared/Typography";
import RequestButton from "@components/shared/RequestButton";

import { useTestDriveTypesSelector } from "@redux/reducers/appConfig";
import { testDriveLocationType } from "@types";
import { TEST_DRIVE_TYPE_ID } from "@shared/constants";

import {
  color,
  fontSize,
  fontWeight,
  hyphenate,
  prop,
} from "@components/shared/utils";

const WrapperContainer = styled.div`
  display: flex;
  padding: 1rem 0;
  border-bottom: 1px solid ${color("grey")};
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ResultItemTopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 1rem;
`;

const ResultItemBottomContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding-left: 1rem;
`;

const ResultItemName = styled(Typography)`
  color: ${prop("color")};
  font-size: ${fontSize("md")};
  font-weight: ${fontWeight("bold")};
  text-transform: uppercase;
  padding-left: 1rem;
`;

const ResultItemDistance = styled(Typography)`
  font-size: ${fontSize("md")};
  margin-left: auto;
  font-weight: ${fontWeight("bold")};
  text-transform: uppercase;
`;

const StyledRequestButton = styled(RequestButton)`
  padding: 0.75rem 0.5rem;
  margin: 0;

  &:enabled:hover {
    background: ${color("white")};
    color: ${color("amiOrange")};
  }
`;

const ResultItemAddress = styled(Typography)`
  padding-right: 0.5rem;
`;

const convertToKilometers = (distanceInMeters, precision = 2) =>
  (distanceInMeters / 1000).toFixed(precision);

const getResultItemAddress = point => {
  return (
    `${point.address1 ?? ""}\n` +
    `${point.zipcode ?? ""} ${point.city ?? ""}\n` +
    `${point.countryName ?? ""}`
  );
};

const FilterResultItem = ({
  testDrivePoint,
  isActive,
  triggerRequestCallback,
  onRequestSuccess,
  onRequestError,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const testDriveTypes = useTestDriveTypesSelector();

  const isHomeTestDrive = testDrivePoint.type === TEST_DRIVE_TYPE_ID.HOME;

  const targetType = testDriveTypes.find(
    type => type.id === testDrivePoint.type
  );

  const color = `#${targetType?.colorHexa}`;

  return (
    <WrapperContainer>
      <LogoContainer>
        <ResultItemLogo testDrivePoint={testDrivePoint} color={color} />
      </LogoContainer>
      <ContentContainer>
        <ResultItemTopContainer>
          <ResultItemName
            data-id={`point-name-${hyphenate(testDrivePoint.name, /\.|\s/g)}`}
            color={color}
          >
            {testDrivePoint.name}
          </ResultItemName>
          {(testDrivePoint.distanceInMeters || isHomeTestDrive) && (
            <ResultItemDistance>{`${convertToKilometers(
              testDrivePoint.distanceInMeters
            )} ${t("general.kilometerSymbol")}`}</ResultItemDistance>
          )}
        </ResultItemTopContainer>
        <ResultItemBottomContainer>
          <ResultItemAddress data-id="test-drive-point-address">
            {getResultItemAddress(testDrivePoint)}
          </ResultItemAddress>
          <StyledRequestButton
            withBorder
            inverted={!isActive}
            data-id="filtered-result-item-select-button"
            customColor={theme.colors.amiOrange}
            triggerRequestCallback={triggerRequestCallback}
            onRequestSuccess={onRequestSuccess}
            onRequestError={onRequestError}
          >
            {isActive ? t("testDrive.selected") : t("testDrive.select")}
          </StyledRequestButton>
        </ResultItemBottomContainer>
      </ContentContainer>
    </WrapperContainer>
  );
};

FilterResultItem.propTypes = {
  triggerRequestCallback: func.isRequired,
  onRequestSuccess: func.isRequired,
  onRequestError: func.isRequired,
  testDrivePoint: testDriveLocationType,
  isActive: bool,
};

export default FilterResultItem;
